import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { SituacaoVendedorEnum } from '@shared/enums/pessoa/situacao-vendedor.enum';
import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { compararValoresOrdenacao } from '@shared/helpers/ordenacao-tabela.helper';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { OportunidadeService } from '@shared/services/apis/vendas/oportunidade.service';
import { OrigemNovaOportunidade } from '@shared/services/models/vendas/origem-nova-oportunidade.model';
import { OportunidadeClienteStruct } from '@shared/services/structs/vendas/oportunidade-cliente.struct';

/**
 * Título "Oportunidades no CRM" pelo cadastro de Pessoa e "Oportunidades do cliente" pelo Processo
 * de Venda; o nome do cliente vai no subtítulo. A origem vai para o CRM montar o texto da Nova oportunidade.
 */
export type OportunidadesClienteDialogData = {
  idPessoaCliente: number;
  nomeCliente: string;
  titulo: string;
  origem: OrigemNovaOportunidade;
};

type OportunidadeClienteLinha = OportunidadeClienteStruct & {
  nomeVendedor: string;
  /** Cor da etapa, com a mesma cor reserva da lista do CRM quando a etapa não tem cor. */
  corEtapaExibida: string;
  origemValor: string;
  isAcaoVencida: boolean;
};

type ColunaOrdenacao = 'codigo' | 'titulo' | 'etapa' | 'vendedor' | 'valor' | 'proximaAcao';
type DirecaoOrdenacao = 'asc' | 'desc';

/**
 * Consulta somente leitura das últimas oportunidades do cliente (AS-01 a AS-04). As ações levam ao
 * CRM pela rota, com o próprio dialog navegando: quem abre não precisa conhecer o CRM.
 */
@Component({
  selector: 'app-oportunidades-cliente-dialog',
  standalone: true,
  imports: [DatePipe, NgTemplateOutlet, MatButtonModule, MatDialogModule, MatIconModule, MatSortModule, MatTableModule, MatTooltipModule,
    AbntPipe, EmptyStateComponent, LoadingBoxDefaultListComponent],
  templateUrl: './oportunidades-cliente-dialog.component.html',
  styleUrls: ['./oportunidades-cliente-dialog.component.css'],
})
export class OportunidadesClienteDialogComponent implements OnInit {
  public oportunidades: OportunidadeClienteLinha[] = [];
  /** As linhas na ordem escolhida, recalculadas só quando os dados carregam ou a ordenação muda. */
  public oportunidadesExibidas: OportunidadeClienteLinha[] = [];
  public isLoading = true;
  public possuiPermissao = false;
  public colunaOrdenacao: ColunaOrdenacao | null = null;
  public direcaoOrdenacao: DirecaoOrdenacao = 'desc';
  public colunasExibidas = ['codigo', 'titulo', 'etapa', 'vendedor', 'valor', 'proximaAcao'];

  constructor(
    private dialogRef: MatDialogRef<OportunidadesClienteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OportunidadesClienteDialogData,
    private router: Router,
    private authService: AuthService,
    private oportunidadeService: OportunidadeService,
    private pessoaLookupService: PessoaLookupService,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    // PA-06: sem permissão o dialog abre com a lista vazia e a orientação, sem consultar a API.
    this.possuiPermissao = !!this.authService.getUserInfos()?.listaPermissoes
      ?.includes(PermissaoEnum.Vendas_Crm_Visualizar);
    if (!this.possuiPermissao) {
      this.isLoading = false;
      return;
    }

    this.carregarOportunidades();
  }

  /** Sem direção (terceiro clique), a coluna sai e volta a ordem da API. */
  public atualizarOrdenacao(ordenacao: Sort): void {
    if (!ordenacao.direction) {
      this.colunaOrdenacao = null;
      this.direcaoOrdenacao = 'desc';
    } else {
      this.colunaOrdenacao = ordenacao.active as ColunaOrdenacao;
      this.direcaoOrdenacao = ordenacao.direction;
    }
    this.aplicarOrdenacao();
  }

  public abrirNoCrm(oportunidade: OportunidadeClienteLinha): void {
    this.dialogRef.close();
    this.router.navigate(['/vendas/crm'], { queryParams: { idOportunidade: oportunidade.idOportunidade } });
  }

  public verTodosNoCrm(): void {
    this.dialogRef.close();
    this.router.navigate(['/vendas/crm'], { queryParams: { idPessoaCliente: this.data.idPessoaCliente } });
  }

  public incluirNoCrm(): void {
    this.dialogRef.close();
    this.router.navigate(['/vendas/crm'], {
      queryParams: {
        novaOportunidadeIdPessoaCliente: this.data.idPessoaCliente,
        origemNovaOportunidade: this.data.origem,
      },
    });
  }

  public fechar(): void {
    this.dialogRef.close();
  }

  private carregarOportunidades(): void {
    forkJoin({
      response: this.oportunidadeService.getUltimasDoCliente(this.data.idPessoaCliente),
      // Sem a lista de vendedores a consulta continua, com "—" no vendedor.
      vendedores: this.pessoaLookupService.listVendedor().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ response, vendedores }) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        // Mesmo nome da tela do CRM: vendedor inativo continua aparecendo, com a marca (RV-06).
        const nomesPorId = new Map<number, string>(
          (vendedores && !vendedores.isError ? vendedores.list ?? [] : [])
            .map(v => [v.idVendedor,
              v.idSituacaoVendedor === SituacaoVendedorEnum.Inativo ? `${v.nome} (inativo)` : v.nome]));

        this.oportunidades = (response.oportunidades ?? []).map(oportunidade => ({
          ...oportunidade,
          nomeVendedor: nomesPorId.get(oportunidade.idVendedorResponsavel) ?? '—',
          corEtapaExibida: oportunidade.corEtapa || '#1E5DB8',
          origemValor: this.descreverOrigemValor(oportunidade),
          isAcaoVencida: this.isAcaoVencida(oportunidade.proximaAcao?.dataPrevista ?? null),
        }));
        this.aplicarOrdenacao();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso',
          'Não foi possível carregar as oportunidades do cliente. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  private descreverOrigemValor(oportunidade: OportunidadeClienteStruct): string {
    switch (oportunidade.vendaTipoDocumento) {
      case VendaTipoDocumentoEnum.Orcamento: return `Orçamento nº ${oportunidade.numeroDocumento ?? ''}`;
      case VendaTipoDocumentoEnum.PedidoDeVenda: return `Pedido nº ${oportunidade.numeroDocumento ?? ''}`;
      default: return 'Estimado';
    }
  }

  /**
   * Mesma regra da tela do CRM: vencida quando a data prevista (data de calendário) é anterior a
   * hoje, comparando no fuso de quem está olhando.
   */
  private isAcaoVencida(dataPrevista: string | null): boolean {
    const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(dataPrevista ?? '');
    if (!partes) return false;

    const data = new Date(Number(partes[1]), Number(partes[2]) - 1, Number(partes[3]));
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return data.getTime() < hoje.getTime();
  }

  /** Sem coluna ativa vale a ordem da API: criação, da mais recente para a mais antiga (AS-02). */
  private aplicarOrdenacao(): void {
    const coluna = this.colunaOrdenacao;
    if (!coluna) {
      this.oportunidadesExibidas = this.oportunidades;
      return;
    }

    // Os números dentro do texto (código) ganham zeros à esquerda para ordenar pelo valor, e não dígito a dígito.
    const chave = (valor: string | number) =>
      typeof valor === 'string' ? valor.replace(/\d+/g, digitos => digitos.padStart(20, '0')) : valor;

    this.oportunidadesExibidas = [...this.oportunidades].sort((primeira, segunda) => {
      const primeiroValor = this.obterValorOrdenacao(primeira, coluna);
      const segundoValor = this.obterValorOrdenacao(segunda, coluna);

      // Sem valor (valor ou próxima ação vazios) fica sempre no fim, nas duas direções.
      if (primeiroValor === null || segundoValor === null) {
        return primeiroValor === segundoValor ? 0 : primeiroValor === null ? 1 : -1;
      }

      const resultado = compararValoresOrdenacao(chave(primeiroValor), chave(segundoValor));
      return this.direcaoOrdenacao === 'asc' ? resultado : -resultado;
    });
  }

  private obterValorOrdenacao(oportunidade: OportunidadeClienteLinha,
    coluna: ColunaOrdenacao): string | number | null {
    switch (coluna) {
      case 'codigo': return oportunidade.codigo;
      case 'titulo': return oportunidade.titulo;
      case 'etapa': return oportunidade.nomeEtapa;
      case 'vendedor': return oportunidade.nomeVendedor;
      case 'valor': return oportunidade.valor;
      // Data prevista "yyyy-MM-dd..." ordena como texto; ação sem data vai para o fim.
      case 'proximaAcao': return oportunidade.proximaAcao?.dataPrevista ?? null;
    }
  }
}
